import { useLayoutEffect, type RefObject } from "react";

export interface AutoHeight {
  height: string;
  overflowY: "auto" | "hidden";
}

/**
 * Logica pura dell'auto-resize (testabile senza DOM): altezza = contenuto
 * (`scrollHeight`) limitata a `maxHeightPx`; oltre quel limite si scrolla.
 */
export function computeAutoHeight(scrollHeightPx: number, maxHeightPx: number): AutoHeight {
  const capped = Math.min(scrollHeightPx, maxHeightPx);
  return {
    height: `${capped}px`,
    overflowY: scrollHeightPx > maxHeightPx ? "auto" : "hidden",
  };
}

/**
 * Adatta l'altezza di un `<textarea>` al contenuto: cresce e si riduce con il
 * testo, fino a `maxHeightPx`; oltre, scrolla. Triggera sul `value` (non solo
 * sull'onChange) cosi' cattura anche i set programmatici del testo — es. il
 * tasto «Traduci la bozza», che riscrive il contenuto del textarea.
 *
 * `useLayoutEffect`: misura e applica prima del paint, niente flicker di altezza.
 * Il floor visivo resta a carico del CSS (`min-h-*`) sul textarea.
 */
export function useAutoResizeTextarea(
  ref: RefObject<HTMLTextAreaElement | null>,
  value: string,
  maxHeightPx = 360,
): void {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // "auto" azzera l'altezza inline prima di rimisurare: senza, lo
    // scrollHeight resterebbe ancorato all'altezza precedente e il textarea
    // non si rimpicciolirebbe quando si cancella testo.
    el.style.height = "auto";
    const { height, overflowY } = computeAutoHeight(el.scrollHeight, maxHeightPx);
    el.style.height = height;
    el.style.overflowY = overflowY;
  }, [ref, value, maxHeightPx]);
}
